/*
 * Co-located server-data hooks for the admin Team surface (U6/U7).
 *
 * The admin-provisioned auth surface now exposes a full member CRUD for the
 * caller's ACTIVE brand: list the roster (`GET /auth/members`), provision a new
 * user (`POST /auth/users`), change a member's role + disabled flag
 * (`PATCH /auth/members/{user_id}`), and remove a membership
 * (`DELETE /auth/members/{user_id}`). All authorization is enforced server-side
 * from a fresh membership read; these hooks just keep the roster query in sync —
 * every mutation invalidates the members list so the table reflects the change.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  createUser,
  listMembers,
  updateMember,
  removeMember,
} from "../../api/endpoints";
import type {
  ProvisionUserRequest,
  UserResponse,
  MemberRosterEntry,
  UpdateMemberRequest,
} from "../../api/client";

/** Query key for the active brand's member roster. */
export const teamKeys = {
  members: ["members"] as const,
} as const;

/** List the active brand's members (admin). 403 for a non-admin. */
export function useMembers(
  options?: Partial<UseQueryOptions<MemberRosterEntry[]>>,
) {
  return useQuery({
    queryKey: teamKeys.members,
    queryFn: () => listMembers(),
    ...options,
  });
}

/** Provision a user in the caller's active brand (admin). 409 if already a member. */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<UserResponse, unknown, ProvisionUserRequest>({
    mutationFn: (body) => createUser(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}

interface UpdateMemberVars {
  userId: string;
  body: UpdateMemberRequest;
}

/**
 * Change a member's role and/or membership-disabled flag (admin). 404 if not a
 * member of this brand; 409 on the last-admin / self-lockout guard.
 */
export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation<MemberRosterEntry, unknown, UpdateMemberVars>({
    mutationFn: ({ userId, body }) => updateMember(userId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}

/** Remove a member's membership in the active brand (admin). 409 last-admin guard. */
export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (userId) => removeMember(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.members });
    },
  });
}
